import React, { lazy, Suspense, useEffect, type ReactNode, type ComponentType } from 'react'
import { NotFound, Route, Switch, AuthRoute } from '@fs/zion-router'
import { AppLayout, colors, useContainerWidth } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext, setLastViewedPid, setLastViewedPersonView } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { SWRConfig } from 'swr'
import { usePageViewEvent } from '@fs/zion-analytics'
import { SelectSiblingPedigreeButton } from '@fs/zion-tree-pedigree'
import { useFeatureFlag } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import { css } from '@emotion/core'
import SubNav from '@fs/zion-tree-subnav-legacy'
import RedirectToPersonPageSubPage from './helpers/RedirectToPersonPageSubPage'
import SourceCentricDetailsOptInOut from './person-page/tabs/SourceCentricDetailsOptInOut'
import PersonAppProviders from './providers/PersonAppProviders'
import CenteredLoadingSpinner from './common/CenteredLoadingSpinner'
import { OneMergePageSkeleton } from './merge-pre/new-merge/OneMergePageSkeleton'
import useGroupOverlay from './helpers/useGroupOverlay'
import usePageDetailPrefix from './hooks/usePageDetailPrefix'
import ShowTempleProvider from './providers/ShowTempleContext'
import PageContentWidthContext, { sizes } from './person-page/PageContentWidthContext'
import { MERGE_ROUTE_PATH } from './merge-pre/new-merge/mergeRouteUtil'
import { PERSON_ROUTE_PATH, PARTIAL_PERSON_ROUTE_PATHS, usePersonPageParams } from './person-page/personPageRouteUtil'
import { CHANGELOG_PATHS } from './changelog/changelogRouteUtil'
import { MERGE_ANALYSIS_ROUTE_PATH } from './merge-post/mergeAnalysisRouteUtil'
import { SPLIT_ANALYSIS_ROUTE_PATH } from './split-analysis/splitAnalysisRouteUtil'
import { NOTES_ROUTE_PATH } from './notes/notesRouteUtil'
import { RESEARCH_HELP_DISMISSED_ROUTE_PATH, RESEARCH_HELP_ROUTE_PATH } from './research-help/researchHelpRouteUtil'
import { PRINT_VIEW_ROUTE_PATH } from './person-print-view/printViewRouteUtil'
import { UNMERGE_ROUTE_PATH } from './unmerge/unmergeRouteUtil'
import { DETAILS_OPT_IN_OUT_PATHS } from './person-page/tabs/detailsOptInOutRouteUtil'
import { useContrastMode } from './ContrastModeProvider'
import NotesPageSkeleton from './notes/NotesPageSkeleton'
import ResearchHelpPageSkeleton from './research-help/ResearchHelpPageSkeleton'
import DismissedHintsPageSkeleton from './research-help/DismissedHintsPageSkeleton'
import PersonPageSkeleton from './person-page/PersonPageSkeleton'
import ChangeLogPageSkeleton from './changelog/ChangeLogPageSkeleton'
import useSourceCentricDetailsFlag from './data-service/useSourceCentricDetailsFlag'
import useNewMergeAnalysisFlag from './data-service/useNewMergeAnalysisFlag'
import useNewMergeFlag from './data-service/useNewMergeFlag'
import useSourceCentricProductHeader from './data-service/useSourceCentricProductHeader'

const mainFlexCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`

// Matches the floating button's bottom offset (--spacer-sm plus the RootsTech promo height when
// present) plus ~48px for the button itself.
const helpAssistantSpacerCss = css`
  height: calc(var(--spacer-sm) + var(--rootstech-promo-height, 0px) + 48px);
  flex-shrink: 0;

  @media print {
    display: none;
  }
`

/**
 * Reserves space at the bottom of the page so content can scroll clear of Root's floating launch
 * buttons (Help Assistant chatbot / guidance panel). Must be rendered inside AppLayout so the
 * reserved space is painted with the page's background color.
 *
 * Note: Root only shows the chatbot button when its `helpChatbot` prop is true (the default) in
 * addition to the help_chatbot_root flag. We don't pass that prop to Root (see index.tsx), so the
 * flag checks below stay in sync with Root — if we ever set helpChatbot={false}, mirror it here.
 */
const HelpAssistantSpacer = (): JSX.Element | null => {
  const showHelpChatbot = useFeatureFlag('help_chatbot_root').isOn
  const showGuidancePanel = useFeatureFlag('shared_complimentaryPanel').isOn
  if (!showHelpChatbot && !showGuidancePanel) return null
  return <div css={helpAssistantSpacerCss} />
}

// TREEWEB-11763 - Safari <16.4 white screen of death
// Lazy-load the overlay to keep the sibling pedigree, but primarily d3-dag
// (transitive dep via SiblingPedigree → dag.js)
// out of the main bundle. d3-dag 1.2.x uses RegExp lookbehind assertions that crash
// Safari < 16.4, and the overlay is only rendered conditionally deep in GroupsProvider.
const SelectSiblingPedigreeOverlay = lazy(() =>
  import('@fs/zion-tree-pedigree').then((m) => ({ default: m.SelectSiblingPedigreeOverlay }))
)

const MergePage = lazy(() => import('./merge-pre/MergePage'))
const UnmergePage = lazy(() => import('./unmerge/UnmergePage'))
const ChangeLogPage = lazy(() => import('./changelog/ChangeLogPage'))
const PersonPage = lazy(() => import('./person-page/PersonPage'))
const ResearchHelpPage = lazy(() => import('./research-help/ResearchHelpPage'))
const DismissedHintsPage = lazy(() => import('./research-help/DismissedHintsPage'))
const NotesPage = lazy(() => import('./notes/NotesPage'))
const DevPage = lazy(() => import('./dev-page/DevPage'))
const PersonPrintViewPage = lazy(() => import('./person-print-view/PersonPrintView'))
const MergeAnalysisPage = lazy(() => import('./merge-post/MergeAnalysis'))
const SplitAnalysisPage = lazy(() => import('./split-analysis/SplitAnalysis'))
const QualityScoreSegmentsPage = lazy(() => import('./quality-score-segments/QualityScoreSegments'))
const PersonPageWrapper = (): JSX.Element => {
  useGroupOverlay()
  const { subPage, pid } = usePersonPageParams('full')
  const { currentTreeId } = useGroupsContext()
  const sendPageView = usePageViewEvent()
  const pageDetailPrefix = usePageDetailPrefix()
  const sourceCentricDetailsFlag = useSourceCentricDetailsFlag()
  const isSourceCentricDetails = subPage === 'details' && sourceCentricDetailsFlag.data?.isOn
  useSourceCentricProductHeader(Boolean(isSourceCentricDetails))

  // page_detail updates link_page for all analytics past this point
  useEffect(() => {
    if (pageDetailPrefix !== undefined) {
      sendPageView({
        page_detail: `${pageDetailPrefix}Person:${subPage}${sourceCentricDetailsFlag.data?.isOn ? ':SourceCentric' : ''}`,
      })
    }
  }, [pageDetailPrefix, sendPageView, subPage, sourceCentricDetailsFlag.data?.isOn])

  useEffect(() => {
    if (currentTreeId) {
      setLastViewedPid('')
    } else {
      setLastViewedPid(pid)
    }
  }, [pid, currentTreeId])

  useEffect(() => {
    subPage && setLastViewedPersonView(subPage)
  }, [subPage])

  return <PersonPage />
}

// Renders the person page when both pid and subPage are present; otherwise
// redirects to the correct full URL (e.g. /:pid → /details/:pid).
const PersonRouteGate = (): JSX.Element => {
  const { pid, subPage } = usePersonPageParams('partial')
  if (pid && subPage) {
    return (
      <PersonAppProviders loadingComponent={<PersonPageSkeleton />}>
        <PersonPageWrapper />
      </PersonAppProviders>
    )
  }
  return <RedirectToPersonPageSubPage />
}

const DEV_PATH = '/'

type GroupsLoadingGateProps = {
  children: ReactNode
  loader: ReactNode
}

/** Gates children on GroupsProvider loading state. Must be rendered inside GroupsProvider. */
const GroupsLoadingGate = ({ children, loader }: GroupsLoadingGateProps): JSX.Element => {
  const { loading } = useGroupsContext()
  if (loading) {
    return <>{loader}</>
  }
  return <>{children}</>
}

type EnhancedTreeRouteProps = {
  path: string | string[]
  backgroundColor?: string
  exact?: boolean
  LoadingComponent: ComponentType
  component?: ComponentType
  children?: ReactNode
  requirePersonalData?: boolean
} & ({ children: ReactNode } | { component: ComponentType })

/**
 * Wrapper around Route that applies the AppLayout and Suspense for lazy loaded components.
 */
const EnhancedTreeRoute = ({
  path,
  backgroundColor,
  exact,
  LoadingComponent,
  ...props
}: EnhancedTreeRouteProps): JSX.Element => (
  <Route path={path} exact={exact}>
    <AppLayout fullWidth backgroundColor={backgroundColor}>
      {/* Tree Route lazy loads ErrorPage without a suspense, so we need to wrap it in suspense here. Also used for the lazy loaded children. */}
      <Suspense fallback={<LoadingComponent />}>
        <TreeRoute {...props} LoadingComponent={LoadingComponent} />
      </Suspense>
      <HelpAssistantSpacer />
    </AppLayout>
  </Route>
)

export default function App(): JSX.Element {
  const atWidth = useContainerWidth()
  const { isOn: advancedUnmergeIsOn } = useFeatureFlag('treeWeb_personR9_advancedUnmerge')
  const [canModifyPrivateSpaceEntities] = usePermission('ModifyAllPrivateSpaceEntitiesPermission')
  const newMergeAnalysisFlag = useNewMergeAnalysisFlag()
  const newMergeIsOn = useNewMergeFlag().data?.isOn
  // Mirror the merge page while it loads; the old merge layout keeps the spinner.
  const MergeRouteLoadingComponent = newMergeIsOn ? OneMergePageSkeleton : CenteredLoadingSpinner
  const { isHighContrast } = useContrastMode()
  const backgroundColorForHighContrast = isHighContrast ? colors.gray05 : colors.gray03
  const isSourceCentricOn = !!useSourceCentricDetailsFlag().data?.isOn

  return (
    <SWRConfig value={{ revalidateOnFocus: process.env.NODE_ENV !== 'development' }}>
      <ErrorBoundary>
        <ShowTempleProvider>
          <PageContentWidthContext.Provider value={atWidth(sizes)}>
            <GroupsProvider
              noLoadingGating
              SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
              SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
            >
              <Switch>
                {/* /tree/person/print-view/ */}
                <EnhancedTreeRoute
                  path={PRINT_VIEW_ROUTE_PATH}
                  exact
                  backgroundColor={colors.gray00}
                  LoadingComponent={CenteredLoadingSpinner}
                >
                  <main id="skipToMainContent" css={mainFlexCss}>
                    <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                      <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                        <Suspense fallback={<CenteredLoadingSpinner />}>
                          <PersonPrintViewPage />
                        </Suspense>
                      </PersonAppProviders>
                    </GroupsLoadingGate>
                  </main>
                </EnhancedTreeRoute>

                <Route path="*">
                  <SubNav page="person" showHelpOthers isDark={isSourceCentricOn} />
                  <main id="skipToMainContent" css={mainFlexCss}>
                    <Switch>
                      {/* Checking NODE_ENV here guarantees that this will be stripped out in the production build. */}
                      {process.env.NODE_ENV === 'development' && (
                        <Route path={DEV_PATH} exact>
                          <AppLayout fullWidth backgroundColor={backgroundColorForHighContrast}>
                            <Suspense fallback={<CenteredLoadingSpinner />}>
                              <DevPage />
                            </Suspense>
                            <HelpAssistantSpacer />
                          </AppLayout>
                        </Route>
                      )}

                      {/* /tree/person/research-help/ — only when source-centric is off; otherwise handled by the person page route */}
                      {!isSourceCentricOn && (
                        <EnhancedTreeRoute
                          path={RESEARCH_HELP_ROUTE_PATH}
                          exact
                          backgroundColor={colors.gray00}
                          LoadingComponent={ResearchHelpPageSkeleton}
                        >
                          <GroupsLoadingGate loader={<ResearchHelpPageSkeleton />}>
                            <PersonAppProviders loadingComponent={<ResearchHelpPageSkeleton />}>
                              <ResearchHelpPage />
                            </PersonAppProviders>
                          </GroupsLoadingGate>
                        </EnhancedTreeRoute>
                      )}

                      {/* /tree/person/research-help/:pid/dismissed - sub page when source-centric is on */}
                      {isSourceCentricOn && (
                        <EnhancedTreeRoute
                          path={RESEARCH_HELP_DISMISSED_ROUTE_PATH}
                          exact
                          backgroundColor={colors.gray00}
                          LoadingComponent={DismissedHintsPageSkeleton}
                        >
                          <GroupsLoadingGate loader={<DismissedHintsPageSkeleton />}>
                            <PersonAppProviders loadingComponent={<DismissedHintsPageSkeleton />}>
                              <DismissedHintsPage />
                            </PersonAppProviders>
                          </GroupsLoadingGate>
                        </EnhancedTreeRoute>
                      )}

                      {/* /tree/person/notes/:pid - sub page when source-centric is on */}
                      {isSourceCentricOn && (
                        <EnhancedTreeRoute
                          path={NOTES_ROUTE_PATH}
                          exact
                          backgroundColor={colors.gray00}
                          LoadingComponent={NotesPageSkeleton}
                        >
                          <GroupsLoadingGate loader={<NotesPageSkeleton />}>
                            <PersonAppProviders loadingComponent={<NotesPageSkeleton />}>
                              <NotesPage />
                            </PersonAppProviders>
                          </GroupsLoadingGate>
                        </EnhancedTreeRoute>
                      )}

                      {/* /tree/person/changelog/ */}
                      <EnhancedTreeRoute
                        path={[...CHANGELOG_PATHS]}
                        exact
                        backgroundColor={colors.gray00}
                        LoadingComponent={ChangeLogPageSkeleton}
                      >
                        <GroupsLoadingGate loader={<ChangeLogPageSkeleton />}>
                          <ChangeLogPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/changelog/merge-analysis/ */}
                      <EnhancedTreeRoute
                        path={MERGE_ANALYSIS_ROUTE_PATH}
                        exact
                        backgroundColor={
                          newMergeAnalysisFlag.data?.isOn && newMergeAnalysisFlag.data.treatment !== 'newMergeV2'
                            ? colors.gray05
                            : colors.gray00
                        }
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <MergeAnalysisPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/changelog/split-analysis/ */}
                      <EnhancedTreeRoute
                        path={SPLIT_ANALYSIS_ROUTE_PATH}
                        exact
                        backgroundColor={colors.gray00}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <SplitAnalysisPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /quality-score/segments */}
                      <EnhancedTreeRoute
                        path="/quality-score/segments"
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        LoadingComponent={CenteredLoadingSpinner}
                      >
                        <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                          <QualityScoreSegmentsPage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/merge/ */}
                      <EnhancedTreeRoute
                        path={MERGE_ROUTE_PATH}
                        exact
                        backgroundColor={newMergeIsOn ? colors.gray00 : colors.gray05}
                        LoadingComponent={MergeRouteLoadingComponent}
                      >
                        <GroupsLoadingGate loader={<MergeRouteLoadingComponent />}>
                          <MergePage />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* /tree/person/unmerge/ */}
                      {advancedUnmergeIsOn && canModifyPrivateSpaceEntities && (
                        <EnhancedTreeRoute
                          path={UNMERGE_ROUTE_PATH}
                          exact
                          backgroundColor={backgroundColorForHighContrast}
                          LoadingComponent={CenteredLoadingSpinner}
                        >
                          <GroupsLoadingGate loader={<CenteredLoadingSpinner />}>
                            <UnmergePage />
                          </GroupsLoadingGate>
                        </EnhancedTreeRoute>
                      )}

                      {/* /tree/person/details/opt-in and /tree/person/details/opt-out */}
                      <AuthRoute path={DETAILS_OPT_IN_OUT_PATHS} exact>
                        <SourceCentricDetailsOptInOut />
                      </AuthRoute>

                      {/* Person Subpages and redirects (details, ordinances, about, sources, etc.) */}
                      <EnhancedTreeRoute
                        path={[PERSON_ROUTE_PATH, ...PARTIAL_PERSON_ROUTE_PATHS]}
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        LoadingComponent={PersonPageSkeleton}
                      >
                        <GroupsLoadingGate loader={<PersonPageSkeleton />}>
                          <PersonRouteGate />
                        </GroupsLoadingGate>
                      </EnhancedTreeRoute>

                      {/* 404 Not Found */}
                      <Route path="*">
                        <AppLayout fullWidth backgroundColor={backgroundColorForHighContrast}>
                          <NotFound />
                          <HelpAssistantSpacer />
                        </AppLayout>
                      </Route>
                    </Switch>
                  </main>
                </Route>
              </Switch>
            </GroupsProvider>
          </PageContentWidthContext.Provider>
        </ShowTempleProvider>
      </ErrorBoundary>
    </SWRConfig>
  )
}
